import React, { useCallback, useEffect, useMemo, useState, createContext, useContext } from 'react';
import { fetchfilters, searchPosts } from './services/common.service';
import { FilterProps, WPSearchResultItem } from './typings';
import Loading from './components/Loading';
import { Filters, FilterTypes, SelectedFilters } from './components/Filters/Filters';
import { NoResult } from './components/NoResult/NoResult';
import * as styles from './App.module.scss';
import { MobileFilters } from './components/MobileFilters/MobileFilters';
import Pagination from './components/Pagination/Pagination';
import { SimilarOpportunities } from './components/SimilarOpportunities/SimilarOpportunities';
import { JobList } from './components/JobList/JobList';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { CarouselFilters } from './components/CarouselFilters/CarouselFilters';
import { CareerBanner } from './components/CareerBanner/CareerBanner';

const { __ } = window.wp.i18n;

// get the filters selected in settings
interface Window {
  cg_jobs_get_filter_data?: FilterTypes;
  cg_jobs_es_var?: { pathName ?: string};
  cg_jobs_country_code?: string;
}
const settingFilters = (window as Window).cg_jobs_get_filter_data;
const countryCodeinSettings = (window as Window).cg_jobs_country_code;
const cCode = countryCodeinSettings ? countryCodeinSettings : (window as Window).cg_jobs_es_var?.pathName;

export const TabFilters = createContext(false);

const AppLanding: React.FC = () => {
  const [jobList, setJobList] = useState<WPSearchResultItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true); 
  const [queryParams, setQueryParams] = useState(new URLSearchParams(location.search));
  const [currentPage, setCurrentPage] = useState<number>(queryParams.get('page') as unknown as number ?? 1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalResult, setTotalResult] = useState<number>(); 
  const [keyword, setKeyword] = useState(queryParams.get('keyword'));
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(window.matchMedia("(max-width: 991px)").matches);
  const [showCarouselFilters, setShowCarouselFilters] = useState<boolean>(window.matchMedia("(max-width: 991px)").matches);
  const [showResults, setShowResults] = useState<boolean>((keyword && keyword?.length >= 3) ? true : false);
  const [resetFilters, setResetFilters] = useState<boolean>(false);
  const [isTabFilterOpen, setIsTabFilterOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<SelectedFilters>({      
      experience_level: new Set<string>(queryParams.getAll('experience_level')),
      location: new Set<string>(queryParams.getAll('location')),
      brand: new Set<string>(queryParams.getAll('brand')),
      professional_communities: new Set<string>(queryParams.getAll('professional_communities')),
      contract_type: new Set<string>(queryParams.getAll('contract_type')),     
  });
  const [jobsIdList, setJobsIdList] = useState<string[]>([]);
  const perPage = 11; 

  const fetchListings = useCallback(async () => {  
    setLoading(true);
    try {      
      // Clean up filters to only include keys with non-empty values
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value instanceof Set ? value.size > 0 : Boolean(value))
      );

      const filtersWithPagination = { 
        filters: {
          ...Object.fromEntries(Object.entries(cleanedFilters).map(([key, value]) => [key, Array.from(value)])), // Convert Set to Array
        },
        page: currentPage, 
        size: perPage, 
        search: keyword ?? "",
        country_code: cCode ?? ""
      };
     //Api call to fetch job
      const data = await searchPosts(filtersWithPagination);
      setJobList(data.data);
      setTotalPages(Math.ceil(data.count / perPage));
      setTotalResult(data.count);
      setJobsIdList(data.data.map(job => job.id));      
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, keyword, perPage]);

  useEffect(() => {   
     // running it in timeout for piano sdk to load
     window.addEventListener('load', () => {
      try {      
        if (keyword) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'internal_search_result.display',
            ise_keyword: keyword,
            ise_page: currentPage,
            ise_engine: 'job_search'
          });
        }
      } catch (e) {
        console.log(e);
      }
    });

    fetchListings();
    
  }, [fetchListings])

  // Add body class in body element to change bg to FEFEFE
  useEffect(() => {
      document.body.classList.add(styles.body);
    
    return () => {
      document.body.classList.remove(styles.body);
    };
  }, []);

  const updateQueryParams = useCallback((key:string, value:string) => {
    const parameters = new URLSearchParams(queryParams);
    parameters.set(key, value);
    
    //Delete empty parameters
    for (const [key, value] of parameters.entries()) {
      if (value === "") {
        parameters.delete(key)       
      }
    }

    const newUrl = `${window.location.pathname}?${parameters.toString()}`;
    window.history.replaceState({}, '', newUrl);
    setQueryParams(parameters);
  }, [queryParams]);

  function smoothScrollTo(targetOffsetTop: number, duration = 500) {
    const start = window.pageYOffset;
    const startTime = performance.now();
  
    function scroll() {
      const currentTime = performance.now();
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const scrollPosition = start + (targetOffsetTop - start) * easeInOutQuad(progress);
      
      window.scrollTo(0, scrollPosition);
  
      if (elapsedTime < duration) {
        requestAnimationFrame(scroll);
      }
    }
  
    function easeInOutQuad(t:number) {
      return t < 0.5
        ? 2 * t * t
        : -1 + (4 - 2 * t) * t;
    }
  
    requestAnimationFrame(scroll);
  }

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page); 
    updateQueryParams('page', page.toString());    
    setTimeout(() => {
      const element = document.querySelector('#container-cjs');
      if (element) {
        const offsetTop = element.getBoundingClientRect().top + window.scrollY; // Calculate the offset
        smoothScrollTo(offsetTop);        
      }                                   
   }, 0);
  
  }, [updateQueryParams]);

  useEffect(() => {
      // Update query parameters for filters and page size
      const params = new URLSearchParams(queryParams);

      // Set filters with values joined by commas
      Object.entries(filters).forEach(([key, value]) => {
        const joinedValues = Array.from(value).join(',');
        if (joinedValues) {
          params.set(key, joinedValues);
        } else {
          params.delete(key);
        }
      });
    
    // Set page and size
    params.set('page', currentPage.toString());
    params.set('size', perPage.toString());
    cCode && params.set('country_code', cCode);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
    setQueryParams(params);
  }, [filters, currentPage, perPage]);

  //Show mobile filters
  useEffect(() => {
    const breadcrumb = document.querySelector('.sub-navigation-wrapper') as HTMLElement;
    const showFilters = () => {
        const backdrop = document.querySelector('.offcanvas-backdrop');
        if (window.matchMedia("(min-width: 992px)").matches) {
            setShowMobileFilters(false);
            setShowCarouselFilters(false);
            backdrop && backdrop.classList.remove('show');
            backdrop && backdrop.classList.add('hide');
        } else {
            setShowMobileFilters(true);
            setShowCarouselFilters(true);
        }

        if (window.matchMedia("(min-width: 1200px)").matches){            
            breadcrumb && (breadcrumb.style.display = 'block');
        } else {            
            breadcrumb && (breadcrumb.style.display = 'none');
        }
    };
    // Set the initial placeholder
    showFilters();  
    // Add an event listener for window resize
    window.addEventListener('resize', showFilters);  
    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', showFilters);
  }, []);

  const handleFiltersChange = useCallback((newFilters: SelectedFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); 
    setResetFilters(false); 
  }, []);

  const handleKeywordUpdate = useCallback((newKeyword: string) => {
    (newKeyword === '') ? setKeyword(null) : setKeyword(newKeyword);
    updateQueryParams('keyword', newKeyword);
    setCurrentPage(1);
    setResetFilters(true);    
  }, [keyword, updateQueryParams]);

  const handleButtonClick = useCallback((newSelectedFilters: Set<string>) => {
   setFilters({...filters, 'experience_level': newSelectedFilters});
   setCurrentPage(1);
   setResetFilters(false); 
  }, []);

  const handleTabToggle = (isOpen: boolean) => {
    setIsTabFilterOpen(isOpen);
  };

  const handleShowResults = useCallback((isVisible: boolean) => {
    setShowResults(isVisible);
  }, []);

  const handleCloseResults = useCallback(() => {
    setShowResults(false);
    setKeyword("");
    setFilters({
      experience_level: new Set<string>(),
      location: new Set<string>(),
      brand: new Set<string>(),
      professional_communities: new Set<string>(),
      contract_type: new Set<string>(),
    });
    setIsTabFilterOpen(false);
  }, []);

  // Memoize filters and callbacks to avoid unnecessary re-renders
  const memoizedFilters = useMemo(() => filters, [filters]);
  const memoizedHandleFiltersChange = useCallback(handleFiltersChange, []);
  const memoizedHandleKeywordUpdate = useCallback(handleKeywordUpdate, [handleKeywordUpdate]);

return (
  <TabFilters.Provider value={isTabFilterOpen}>
    <div className={[styles['header-mtop'], 'container'].join(' ')} id="container-cjs">
      <CareerBanner currentKeyword={keyword ?? ""} onSearch={memoizedHandleKeywordUpdate} onTabFilterOpen={handleTabToggle} results={handleShowResults}/> 
        {showResults && <div className={styles['job-listing-section']} id="job-listing-section">
          {((showCarouselFilters && settingFilters?.includes('experience_level'))) && <CarouselFilters onFilterSelect={handleButtonClick} initialFilters={filters} keyword={keyword ?? ""} reset={resetFilters} />}
          {(totalResult && totalResult > 0) ? (
            showMobileFilters ? 
            <div className={styles['search-results']}>
              <div>We found {totalResult} results</div>
              <button onClick={handleCloseResults} className={styles['close-icon']} aria-label="Close search results"></button>
            </div>
            :
            <div className={styles['search-results']}>
              <div>We found {totalResult} results matching your search</div>
              <button onClick={handleCloseResults}>Close search results</button>
            </div>
            ): ''}
              <div className={[styles['jobs-wrapper-cjs']].join(" ")} id="wrapper-cjs">   
                <div id="job-list-section">
                  {loading ? (
                    <Loading />
                  ) : 
                  <>                                          
                    {jobList.length === 0 ? (
                      <div>
                        <NoResult />
                        <SimilarOpportunities experienceLevel={Array.from(filters.experience_level)} professionalCommunity={Array.from(filters.professional_communities)} contractType={Array.from(filters.contract_type)}/>
                      </div>
                    ) : jobList.length < 6 && totalPages === 1 ? (
                      <div>
                        <div className={styles['one-result']}><JobList jobs={jobList} /></div>
                        <SimilarOpportunities experienceLevel={Array.from(filters.experience_level)} professionalCommunity={Array.from(filters.professional_communities)} contractType={Array.from(filters.contract_type)} jobsIdList={jobsIdList}/>
                      </div>
                    ) : (
                      <JobList jobs={jobList}/>
                    )}     
                      </>
                  }
                </div>
                {        
                  (showMobileFilters) 
                  ? <MobileFilters initialFilters={memoizedFilters} onFiltersChange={memoizedHandleFiltersChange} totalResult={totalResult ? totalResult : 0} settingFilters={settingFilters} keyword={keyword ?? ""} reset={resetFilters}/>                                     
                  : <Filters initialFilters={memoizedFilters} onFiltersChange={memoizedHandleFiltersChange} settingFilters={settingFilters} resetFilters={resetFilters} keyword={keyword ?? ""} />              
                }
                                                                    
              </div>  
              <div className={styles['global-site-link-section']}>   
                <a href="https://www.capgemini.com/careers/join-capgemini/job-search/?size=15" target='_blank'>{ __( 'Go to the global site', 'cg-jobs' ) }</a>
                {totalPages > 1 ? <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange} />: <div className={styles['empty']}></div>}              
              </div>   
        </div>
        }
    </div>
    </TabFilters.Provider>
  );
}

export default React.memo(AppLanding);
